import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import ChatResponseRenderer from "../components/Common/ChatResponseRenderer";
import config from "../config";
import "./Claudechat.css";

const API_BASE_URL = config.API_BASE_URL;
const SESSION_STORAGE_KEY = "claude_chat_session_id";

const GREETING = `Hey there! 👋 I'm **ThriftOps AI** — your resale operations assistant.

Here's what I can help you with:

- **Inventory intake** — barcode setup, digitizing your intake flow
- **AI-powered listings** — photo-to-listing, cross-listing to eBay, Depop, Shopify
- **Smart pricing** — condition-based, brand-aware pricing with real-time comps
- **Warehouse management** — 1/1 barcode tracking, location-based stock
- **Order fulfillment** — pick-lists, shipping labels, live order sync

The more detail you give me, the better I can help. What would you like to know?`;

function makeSessionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `claude-session-${Date.now()}`;
}

const TypingIndicator = () => (
  <div className="chatbot-message model">
    <div className="chatbot-message-text-wrap">
      <div className="chatbot-message-text">
        <span className="typing-dots">
          <span />
          <span />
          <span />
        </span>
      </div>
    </div>
  </div>
);

const EmptyState = ({ isLimitReached, onSuggestion }) => (
  <div className="chatbot-empty-state">
    <div className="chatbot-empty-logo">T</div>
    <h1 className="chatbot-empty-title">Where should we begin?</h1>
    <div className="chatbot-suggestions">
      {[
        "How do I set up inventory intake with barcodes?",
        "How does AI-powered listing work?",
        "Explain smart pricing algorithm",
        "How do I manage warehouse locations?",
      ].map((s) => (
        <button
          key={s}
          className="chatbot-suggestion-card"
          onClick={() => !isLimitReached && onSuggestion(s)}
          disabled={isLimitReached}
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

const LoginRequiredModal = ({ isOpen, onClose, onLogin }) => {
  if (!isOpen) return null;
  return (
    <div className="chatbot-login-modal-overlay">
      <div className="chatbot-login-modal">
        <h3>🔒 Free Limit Reached</h3>
        <p>
          You've used all <strong>3 free messages</strong> from this device
          today. Please login to continue chatting.
        </p>
        <div className="chatbot-login-modal-buttons">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onLogin}>
            Login
          </button>
        </div>
      </div>
    </div>
  );
};

const ClaudeChat = () => {
  const { companyId, isAuthenticated } = useAuth();

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [deviceChatCount, setDeviceChatCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [remainingMessages, setRemainingMessages] = useState(3);
  const [userMessageCount, setUserMessageCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [userLimitRemaining, setUserLimitRemaining] = useState(10);
  const [userHasUnlimited, setUserHasUnlimited] = useState(false);
  const [loading, setLoading] = useState(true);
  const listRef = useRef(null);
  const restoredRef = useRef(false);
  const isGuest = !isAuthenticated();

  const prevGuestCount = useRef(deviceChatCount);

  const handleOverlayClick = () => setSidebarOpen(false);
  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  const refreshGuestCount = useCallback(async () => {
    if (!isGuest) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/claude-chat/guest/check-limit`,
      );
      const data = await response.json();
      setDeviceChatCount(data.count);
      setRemainingMessages(data.remaining);
      return data;
    } catch (error) {
      console.error("Failed to refresh guest count:", error);
      return null;
    }
  }, [isGuest]);

  // Init limit tracking
  useEffect(() => {
    const initTracking = async () => {
      if (!isGuest) {
        setLoading(false);
        if (isAuthenticated() && companyId) {
          try {
            const response = await api.get(
              `/api/claude-chat/${companyId}/check-limit`,
            );
            const data = response.data;
            setUserMessageCount(data.count || 0);
            setUserLimitRemaining(data.remaining || 10);
            setUserHasUnlimited(data.unlimited || false);
          } catch (error) {
            console.error("Failed to check user limit:", error);
          }
        }
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/claude-chat/guest/check-limit`,
        );
        const data = await response.json();
        setDeviceChatCount(data.count);
        setRemainingMessages(data.remaining);
        if (!data.canChat && isGuest) setShowLoginModal(true);
      } catch (error) {
        console.error("Failed to init guest tracking:", error);
      } finally {
        setLoading(false);
      }
    };
    initTracking();
  }, [isGuest, isAuthenticated, companyId]);

  // Auto-trigger login modal when guest hits limit
  useEffect(() => {
    if (
      isGuest &&
      prevGuestCount.current < 3 &&
      deviceChatCount >= 3 &&
      !showLoginModal
    ) {
      setShowLoginModal(true);
    }
    prevGuestCount.current = deviceChatCount;
  }, [isGuest, deviceChatCount, showLoginModal]);

  // Dismiss login modal if user logs in
  useEffect(() => {
    if (!isGuest && showLoginModal) setShowLoginModal(false);
  }, [isGuest, showLoginModal]);

  // Session init
  useEffect(() => {
    if (loading) return;
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      setSessionId(existing);
      restoredRef.current = true;
    } else {
      const id = makeSessionId();
      setSessionId(id);
      localStorage.setItem(SESSION_STORAGE_KEY, id);
      restoredRef.current = true;
    }
  }, [loading]);

  // Load existing messages for authenticated users
  useEffect(() => {
    if (!isAuthenticated()) return;
    if (!restoredRef.current || !sessionId || !companyId) return;
    restoredRef.current = false;
    api
      .get(`/api/claude-chat/${companyId}/sessions/${sessionId}`)
      .then((res) => {
        if (res?.data?.success && res.data.messages.length > 0) {
          const loaded = res.data.messages.map((m) => ({
            role: m.role,
            text: m.message || "",
            createdAt: m.created_at,
          }));
          if (loaded.length > 0) setMessages(loaded);
        }
      })
      .catch(() => {});
  }, [sessionId, companyId, isAuthenticated]);

  // Auto-scroll
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const fetchSessions = useCallback(async () => {
    if (!isAuthenticated() || !companyId) return;
    setLoadingSessions(true);
    try {
      const res = await api.get(`/api/claude-chat/${companyId}/sessions`);
      if (res?.data?.success) setSessions(res.data.sessions || []);
    } catch (_) {}
    setLoadingSessions(false);
  }, [companyId, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated()) fetchSessions();
  }, [fetchSessions, isAuthenticated]);

  const handleNewChat = useCallback(async () => {
    if (isGuest) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/claude-chat/guest/check-limit`,
        );
        const data = await response.json();
        if (!data.canChat) {
          setShowLoginModal(true);
          return;
        }
      } catch (_) {
        return;
      }
    } else if (isAuthenticated() && companyId) {
      try {
        const response = await api.get(
          `/api/claude-chat/${companyId}/check-limit`,
        );
        const data = response.data;
        if (!data.canChat && !data.unlimited) return;
      } catch (_) {
        return;
      }
    }

    const id = makeSessionId();
    setSessionId(id);
    localStorage.setItem(SESSION_STORAGE_KEY, id);
    setMessages([]);
    setSelectedImage(null);
    setMessage("");
    closeSidebarOnMobile();
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [isGuest, isAuthenticated, companyId]);

  const loadSession = useCallback(
    async (session) => {
      if (!isAuthenticated()) return;
      try {
        const res = await api.get(
          `/api/claude-chat/${companyId}/sessions/${session.id}`,
        );
        if (res?.data?.success) {
          const loaded = res.data.messages.map((m) => ({
            role: m.role,
            text: m.message || "",
            createdAt: m.created_at,
          }));
          setMessages(loaded.length > 0 ? loaded : []);
          setSessionId(session.id);
          localStorage.setItem(SESSION_STORAGE_KEY, session.id);
          setSelectedImage(null);
          setMessage("");
          closeSidebarOnMobile();
        }
      } catch (_) {}
    },
    [companyId, isAuthenticated],
  );

  const isLimitReached = useMemo(() => {
    if (isAuthenticated()) return !userHasUnlimited && userMessageCount >= 10;
    return deviceChatCount >= 3;
  }, [isAuthenticated, userHasUnlimited, userMessageCount, deviceChatCount]);

  const canSend = useMemo(() => {
    if (loading) return false;
    if (isAuthenticated()) {
      if (!userHasUnlimited && userMessageCount >= 10) return false;
      return (
        Boolean(companyId) &&
        Boolean(sessionId) &&
        (message.trim().length > 0 || Boolean(selectedImage)) &&
        !sending
      );
    }
    return (
      !isLimitReached &&
      Boolean(sessionId) &&
      (message.trim().length > 0 || Boolean(selectedImage)) &&
      !sending
    );
  }, [
    isAuthenticated,
    companyId,
    sessionId,
    message,
    selectedImage,
    sending,
    isLimitReached,
    loading,
    userHasUnlimited,
    userMessageCount,
  ]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage({
        base64: e.target.result,
        mimeType: file.type || "image/jpeg",
        previewUrl: e.target.result,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleRemoveImage = () => setSelectedImage(null);
  const handleSuggestion = (text) => {
    setMessage(text);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const isFirstUserMessage =
    messages.filter((m) => m.role === "user").length === 0;

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !selectedImage) return;
    if (loading || sending) return;

    // Limit checks
    if (isGuest) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/claude-chat/guest/check-limit`,
        );
        const data = await response.json();
        if (!data.canChat) {
          setShowLoginModal(true);
          return;
        }
      } catch (_) {
        return;
      }
    } else if (isAuthenticated() && companyId) {
      try {
        const response = await api.get(
          `/api/claude-chat/${companyId}/check-limit`,
        );
        const data = response.data;
        if (!data.canChat && !data.unlimited) return;
      } catch (_) {
        return;
      }
    }

    if (!sessionId) return;

    const imageToSend = selectedImage;
    const userMsg = {
      role: "user",
      text: trimmedMessage,
      imagePreview: selectedImage ? selectedImage.previewUrl : null,
      createdAt: new Date().toISOString(),
    };

    // First message — show greeting
    if (isFirstUserMessage) {
      setMessages([
        userMsg,
        { role: "model", text: GREETING, createdAt: new Date().toISOString() },
      ]);
      setMessage("");
      setSelectedImage(null);

      if (isGuest) {
        try {
          await fetch(`${API_BASE_URL}/api/claude-chat/guest/increment-count`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });
          await refreshGuestCount();
        } catch (_) {}
      }
      return;
    }

    setMessages((prev) => [...prev, userMsg]);
    setMessage("");
    setSelectedImage(null);
    setSending(true);

    try {
      let response;
      const payload = {
        message: trimmedMessage,
        session_id: sessionId,
        image: imageToSend ? imageToSend.base64 : undefined,
        mimeType: imageToSend ? imageToSend.mimeType : undefined,
      };

      if (isGuest) {
        const fetchResponse = await fetch(
          `${API_BASE_URL}/api/claude-chat/guest`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        const data = await fetchResponse.json();
        response = { data };
        if (data?.success) await refreshGuestCount();
      } else {
        response = await api.post(`/api/claude-chat/${companyId}`, payload);

        if (response?.data?.success) {
          try {
            const limitRes = await api.get(
              `/api/claude-chat/${companyId}/check-limit`,
            );
            const limitData = limitRes.data;
            setUserMessageCount(limitData.count);
            setUserLimitRemaining(limitData.remaining);
            setUserHasUnlimited(limitData.unlimited || false);
          } catch (_) {}
          fetchSessions();
        }
      }

      if (response?.data?.success) {
        setMessages((prev) => [
          ...prev,
          {
            role: "model",
            text: response.data.reply || "No reply received.",
            createdAt: new Date().toISOString(),
          },
        ]);
      } else {
        if (response?.data?.message?.includes("Guest limit reached")) {
          await refreshGuestCount();
          setShowLoginModal(true);
        }
        setMessages((prev) => [
          ...prev,
          {
            role: "model",
            text:
              response?.data?.message ||
              "I couldn't process that request right now. Please try again.",
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message || error?.message;
      if (serverMsg?.includes("Guest limit reached")) {
        await refreshGuestCount();
        setShowLoginModal(true);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "model",
          text:
            serverMsg ||
            "Chat service is unavailable right now. Please try again shortly.",
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const handleLoginRedirect = () => {
    setShowLoginModal(false);
    localStorage.setItem("chatbot_source", "true");
    window.location.href = "/login?from=chatbot";
  };

  if (loading) {
    return (
      <div
        className="chatbot-page"
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          Loading...
        </div>
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="chatbot-page" style={{ height: "100%", maxHeight: "100%" }}>
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLoginRedirect}
      />

      <button
        type="button"
        className="chatbot-mobile-menu-btn"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open chat history"
      >
        ☰
      </button>

      <div
        className={`chatbot-sidebar-overlay ${sidebarOpen ? "overlay-visible" : ""}`}
        onClick={handleOverlayClick}
      />

      <aside
        className={`chatbot-history-sidebar ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <button
          type="button"
          className="chatbot-new-chat-btn"
          onClick={handleNewChat}
        >
          + New Chat
        </button>

        {isGuest && (
          <div className="chatbot-sidebar-guest">
            <div className="chatbot-guest-dots">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`chatbot-guest-dot ${i < deviceChatCount ? "used" : ""}`}
                />
              ))}
            </div>
            <span className="chatbot-guest-label">
              {isLimitReached
                ? "Free limit reached (3/3)"
                : `${deviceChatCount}/3 free messages used today`}
            </span>
            <button
              className="chatbot-guest-login-btn"
              onClick={handleLoginRedirect}
            >
              Login for unlimited →
            </button>
          </div>
        )}

        {isAuthenticated() && (
          <>
            <div className="chatbot-history-label">Recent Chats</div>
            <div className="chatbot-session-list">
              {loadingSessions ? (
                <p className="chatbot-no-sessions">Loading...</p>
              ) : sessions.length === 0 ? (
                <p className="chatbot-no-sessions">No history yet.</p>
              ) : (
                sessions.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    className={`chatbot-session-item ${sessionId === s.id ? "active" : ""}`}
                    onClick={() => loadSession(s)}
                    title={s.title}
                  >
                    <span className="chatbot-session-item-title">
                      {s.title}
                    </span>
                    <span className="chatbot-session-item-date">
                      {formatDate(s.updated_at)}
                    </span>
                  </button>
                ))
              )}
            </div>

            {!userHasUnlimited && (
              <div
                className="chatbot-sidebar-guest"
                style={{ marginTop: "auto", marginBottom: "1rem" }}
              >
                <div className="chatbot-guest-dots">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                    <div
                      key={i}
                      className={`chatbot-guest-dot ${i < userMessageCount ? "used" : ""}`}
                      style={{ width: "5px", height: "5px" }}
                    />
                  ))}
                </div>
                <span className="chatbot-guest-label">
                  {userMessageCount >= 10
                    ? "Daily limit reached (10/10)"
                    : `${userMessageCount}/10 messages used today`}
                </span>
              </div>
            )}
          </>
        )}
      </aside>

      <div className="chatbot-main">
        <div className="chatbot-message-list" ref={listRef}>
          {!hasMessages ? (
            <EmptyState
              isLimitReached={isLimitReached}
              onSuggestion={handleSuggestion}
            />
          ) : (
            <div className="chatbot-messages-inner">
              {messages.map((item, index) => (
                <div
                  key={`${item.role}-${index}`}
                  className={`chatbot-message ${item.role}`}
                >
                  {item.role === "model" ? (
                    <div className="chatbot-message-text-wrap">
                      <div className="chatbot-message-text">
                        {item.text && (
                          <ChatResponseRenderer
                            text={item.text}
                            role={item.role}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {item.imagePreview && (
                        <img
                          src={item.imagePreview}
                          alt="uploaded"
                          className="chatbot-message-image"
                        />
                      )}
                      {item.text && (
                        <div className="chatbot-message-text">
                          <ChatResponseRenderer
                            text={item.text}
                            role={item.role}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {sending && <TypingIndicator />}
            </div>
          )}
        </div>

        <div className="chatbot-input-wrap">
          {selectedImage && (
            <div className="chatbot-image-preview-wrap">
              <img
                src={selectedImage.previewUrl}
                alt={selectedImage.name}
                className="chatbot-image-preview"
              />
              <button
                type="button"
                className="chatbot-image-remove"
                onClick={handleRemoveImage}
                title="Remove image"
              >
                &times;
              </button>
            </div>
          )}
          <div className="chatbot-input-row">
            <textarea
              ref={textareaRef}
              placeholder={
                isLimitReached
                  ? "Limit reached. Please login to continue."
                  : "Ask me anything about ThriftOps..."
              }
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={sending || isLimitReached}
            />
            <div className="chatbot-input-actions">
              <button
                type="button"
                className="chatbot-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || isLimitReached}
                title="Attach image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21.44 11.05L12.25 20.24C9.81 22.68 5.85 22.68 3.41 20.24C0.97 17.8 0.97 13.84 3.41 11.4L12.6 2.21C14.22 0.59 16.85 0.59 18.47 2.21C20.09 3.83 20.09 6.46 18.47 8.08L9.63 16.92C8.82 17.73 7.5 17.73 6.69 16.92C5.88 16.11 5.88 14.79 6.69 13.98L14.82 5.85"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageSelect}
              />
              <button
                type="button"
                className="chatbot-send-btn"
                onClick={handleSend}
                disabled={!canSend}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaudeChat;
